import { Helmet } from 'react-helmet-async'

import { faHome } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import ListBlankState from '@components/ListBlankState'
import PageHeader from '@components/PageHeader'

import { groupEntitiesByEdition } from '@functions/publicationEdition'
import { sanitiseObject } from '@functions/utility'

import { generateUrls } from '@hooks/useOrder'
import useOrders from '@hooks/useOrders'
import useSelector from '@hooks/useSelector'
import useStatuses from '@hooks/useStatuses'
import type { OrderFilters } from '@hooks/useOrders'

import NewsDeskFilters from '@sections/Admin/packs/NewsDesk/components/NewsDeskFilters'

import PublicationEditionGroup from '../PublicationEditionGroup'

const allowedFilters = ['brand_id', 'publication_id', 'publication_edition_id', 'status_id', 'string']

interface PageContentProps {
  pageFilters: OrderFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const sanitisedFilters = sanitiseObject(pageFilters, allowedFilters)

  const ordersPayload = useOrders({
    filters: {
      order_type: 'editorial',
      ...sanitisedFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      // @ts-expect-error TS2561
      include_order_agency: true,
      include_order_campaign: true,
      include_order_publication: true,
      include_order_publication_edition: true,
      include_order_status: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredOrders,
    filteredOrdersCount,
    loading,
  } = ordersPayload

  const statusesPayload = useStatuses({ filters: { status_keys: ['approved', 'pending', 'processing'] } })
  const { filteredStatuses } = statusesPayload

  const publicationEditions = useSelector(reduxState => reduxState.entities.publicationEditions)

  const groupedOrdersByEdition = groupEntitiesByEdition({
    callbacks: {
      getPrimaryGroupId: entity => entity.publication_edition_id,
      getSecondaryGroupId: entity => entity.agency_id,
    },
    entities: filteredOrders,
    publicationEditions,
    secondaryGroupKey: 'agencies',
  })

  const hasGroupedOrdersByEdition = !!groupedOrdersByEdition.length

  const { editorialsUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Editorials | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="editorials"
        nestedNavigation
        tabBarItems={[
          {
            href: editorialsUrl,
            icon: faHome,
            key: 'editorials',
            title: 'EDITORIALS',
          },
        ]}
        title="Editorials"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasGroupedOrdersByEdition && <ListBlankState />}

            {!loading && hasGroupedOrdersByEdition && (
              Object.values(groupedOrdersByEdition).map(group => (
                <PublicationEditionGroup
                  key={group.id}
                  editionAgencies={group.agencies}
                  orderCount={group.entityCount}
                  publicationEditionId={group.id}
                />
              ))
            )}

            {!loading && (
              <Box variant="white">
                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredOrdersCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </Box>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NewsDeskFilters
              allowedFilters={allowedFilters}
              pageFilters={pageFilters}
              statuses={filteredStatuses}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
